/* useContentLock — drop into any page whose content should be harder to copy.
   Front-end deterrent only — the real defence is the server-side stage gate
   + signed quiz delivery. This hook adds the friction layer that discourages
   casual copy/screen-grab/print.

   Use:
     useContentLock({ enabled: true, watermark: user?.email })

   What it does on mount:
     • disables right-click contextmenu
     • disables Ctrl/Cmd + P / S / U / C (the obvious copy paths)
     • disables text-selection on the body (CSS class)
     • adds a fixed-position diagonal watermark with the supplied label
*/
import { useEffect } from 'react';

const KEY_BLOCK = new Set(['p', 's', 'u', 'c']);

export const useContentLock = ({ enabled = true, watermark = '' } = {}) => {
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return undefined;

    const onContext = (e) => { e.preventDefault(); };
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && KEY_BLOCK.has((e.key || '').toLowerCase())) {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', onContext);
    document.addEventListener('keydown', onKey);

    // No-select class on body
    document.body.classList.add('content-locked-noselect');

    // Watermark layer
    let wmEl = null;
    if (watermark) {
      wmEl = document.createElement('div');
      wmEl.className = 'content-locked-watermark';
      const text = `${watermark} · ${new Date().toISOString().slice(0,10)}`;
      wmEl.style.cssText = `
        position: fixed; inset: 0; pointer-events: none; z-index: 40;
        background-image: repeating-linear-gradient(
          -30deg,
          transparent 0,
          transparent 220px,
          rgba(0,0,0,0.04) 220px,
          rgba(0,0,0,0.04) 222px
        );
      `;
      const inner = document.createElement('div');
      inner.style.cssText = `
        position: absolute; inset: 0; display: grid;
        grid-template-columns: repeat(4, 1fr); grid-template-rows: repeat(6, 1fr);
        place-items: center; opacity: 0.07; pointer-events: none;
        font-size: 12px; color: #000; font-family: ui-sans-serif, system-ui;
        transform: rotate(-30deg);
      `;
      for (let i = 0; i < 24; i++) {
        const span = document.createElement('span');
        span.textContent = text;
        span.style.cssText = 'white-space: nowrap;';
        inner.appendChild(span);
      }
      wmEl.appendChild(inner);
      document.body.appendChild(wmEl);
    }

    // DevTools blur removed — the outer/inner window-size heuristic falsely
    // triggered inside embedded previews, split-screen and zoomed browsers.

    return () => {
      document.removeEventListener('contextmenu', onContext);
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('content-locked-noselect');
      if (wmEl) wmEl.remove();
    };
  }, [enabled, watermark]);
};

export default useContentLock;
